import { AxiosRequestConfig, GenericAbortSignal } from 'axios'
import { API_ATTENDANCE_URL, API_BASIC_URL } from '../constants/base-urls'
import type {
  IAttendanceManagment,
  IAttendanceManagmentDetail,
  IAttendanceRequest,
  IAttendanceRequestDetail,
  IAttendanceStatus,
  IEmployeeHistoryAttendance,
  IEmployeeLeave,
  IEmployeeOvertime,
  IHoliday,
  IHolidayInRange,
  IHolidayType,
  ILeave,
  IOvertimeDataTable,
  IOvertimeDetail,
  IPaginationParam,
  IPaginationParamAttendaceRequest,
  IPaginationResponse,
  IPaginationShiftManagementParam,
  ISchedule,
  IShiftManagementIndex,
  IShiftManagementSchedule,
  IShiftManagementSwitch,
  IShiftPattern,
  IShiftPolicy,
  IShiftPolicyDetail,
  IStatistic,
  ITimezone,
} from '../types'
import { createAxiosInstance } from '../utils'

const axios = createAxiosInstance({ baseURL: API_ATTENDANCE_URL, withAuth: true })
const axiosBasic = createAxiosInstance({ baseURL: API_BASIC_URL, withAuth: true })

type FetchAttendanceParams = {
  logType?: string
  branchId?: string
  employeeId?: string
  endDate?: string
  isInOffice?: string
  isLate?: string
  startDate?: string
} & IPaginationParam

/**
 * Schedule
 *
 */
export const fetchSchedules = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ISchedule> }>(`/schedule`, { params, signal }).then((response) => response.data.data)
}

export const fetchSchedule = (oid: string) => {
  return axios.get<{ data: ISchedule }>(`/schedule/${oid}`).then((response) => response.data.data)
}

export const createSchedule = (payload: Record<string, any>) => {
  return axios.post<{ data: ISchedule }>(`/employer/schedule`, payload).then((response) => response.data.data)
}

export const updateSchedule = (id?: string, payload?: Record<string, any>) => {
  return axios.put<{ data: ISchedule }>(`/employer/schedule/${id}`, payload).then((response) => response.data.data)
}

export const deleteSchedule = (id?: string) => {
  return axios.delete(`/employer/schedule/${id}`).then((response) => response.data.data)
}

/**
 * Timezone
 *
 */
export const fetchTimezones = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ITimezone> }>(`/timezone`, { params, signal }).then((response) => response.data.data)
}

export const fetchTimezoneBasic = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axiosBasic.get<{ data: IPaginationResponse<ITimezone> }>(`/timezone`, { params, signal }).then((response) => response.data.data)
}

export const fetchAttendanceManagement = (params?: FetchAttendanceParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployeeHistoryAttendance> }>(`/employer/logs`, { params, signal })
    .then((response) => response.data.data)
}

/**
 * Request Management
 *
 */
export const fetchRequestManagement = (params?: FetchAttendanceParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IPaginationResponse<ILeave> }>(`/employer/leave`, { params, signal }).then((response) => response.data.data)
}

export const updateRequest = (payload: Record<string, any>) => {
  return axios.post(`/employer/leave/update-status`, payload).then((response) => response.data.data)
}

export const fetchStatistic = (params?: FetchAttendanceParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IStatistic[] }>(`/employer/logs/statistics`, { params, signal }).then((response) => response.data.data)
}

export const updateAttendance = (payload: Record<string, any>) => {
  return axios.post(`/employer/update-status`, payload).then((response) => response.data.data)
}

export const downloadAttendance = (oid: string, payload: Record<string, any>) => {
  const queryString = new URLSearchParams(payload).toString()
  const url = `employer/logs/export/?employeeId=${oid}&${queryString}`
  return axios.get(url).then((response) => response.data)
}

/**
 * Request Management
 *
 */
export const fetchEmployeeLeave = (params: { employeeId: string; endDate?: string; startDate?: string } & IPaginationParam) => {
  params = { endDate: '2030-01-01', startDate: '2024-01-01', ...params }
  return axios.get<{ data: IPaginationResponse<IEmployeeLeave> }>(`/employer/leave`, { params }).then((response) => response.data.data)
}

export const fetchReportStatistic = (oid: string, payload: Record<string, any>) => {
  const queryString = new URLSearchParams(payload).toString()
  const url = `/employer/logs/report-statistics/${oid}?${queryString}`
  return axios.get<{ data: IStatistic[] }>(url).then((response) => response.data.data)
}

export const exportToExcel = (params: { startDate?: string; endDate?: string; branchId?: string; departmentId?: string }) => {
  return axios.get(`/employer/logs/export`, { params }).then((response) => response.data)
}

/**
 * Request Management
 *
 */
export const fetchAttendanceRequests = (params?: IPaginationParamAttendaceRequest, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IAttendanceRequest> }>(`/attendance-request`, { params, signal })
    .then((response) => response.data.data)
}

export const createAttendanceRequest = (payload: Record<string, any>) => {
  return axios.post(`/attendance-request`, payload).then((response) => response.data.data)
}

export const updateAttendanceRequest = (oid: string, payload: Record<string, any>) => {
  return axios.put(`/attendance-request/${oid}`, payload).then((response) => response.data.data)
}

export const fetchAttendanceRequest = (oid: string, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IAttendanceRequestDetail }>(`/attendance-request/${oid}`, { signal }).then((response) => response.data.data)
}

export const updateStatusAttendanceRequest = (oid: string, payload: Record<string, any>) => {
  return axios.patch(`/attendance-request/approval/${oid}`, payload).then((response) => response.data.data)
}

/**
 * Request Management
 *
 */
export const fetchAttendanceManagements = (params?: IPaginationParamAttendaceRequest, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IAttendanceManagment> }>(`/attendance`, { params, signal })
    .then((response) => response.data.data)
}

export const createAttendanceManagement = (payload: Record<string, any>) => {
  return axios.post(`/attendance`, payload).then((response) => response.data.data)
}

export const updateAttendanceManagement = (oid: string, payload: Record<string, any>) => {
  return axios.put(`/attendance/${oid}`, payload).then((response) => response.data.data)
}

export const fetchAttendanceManagementDetail = (oid: string, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IAttendanceManagmentDetail }>(`/attendance/${oid}`, { signal }).then((response) => response.data.data)
}

export const downloadAttendanceManagement = (payload: Record<string, any>) => {
  return axios
    .get(`/attendance/export`, {
      params: payload,
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    .then((response) => {
      return response
    })
}
/**
 * Holiday
 *
 */
export const fetchHolidays = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IPaginationResponse<IHoliday> }>(`/holiday`, { ...config, params }).then((response) => response.data.data)
}

export const fetchHoliday = (id: string, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IHoliday }>(`/holiday/${id}`, config).then((response) => response.data.data)
}

export const createHoliday = (payload: Record<string, any>) => {
  return axios.post<{ data: IHoliday }>(`/holiday`, payload).then((response) => response.data.data)
}

export const updateHoliday = (id: string, payload: Record<string, any>) => {
  return axios.put<{ data: IHoliday }>(`/holiday/${id}`, payload).then((response) => response.data.data)
}

export const deleteHoliday = (id: string) => {
  return axios.delete(`/holiday/${id}`).then((response) => response.data.data)
}

export const fetchHolidayType = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IHolidayType[] }>(`/master/holiday-type`, { ...config, params }).then((response) => response.data.data)
}

export const fetchEventType = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IHolidayType[] }>(`/master/event-type`, { ...config, params }).then((response) => response.data.data)
}

export const fetchHolidayInRange = (params?: { startDate?: string; endDate?: string }) => {
  return axios.get<{ data: IHolidayInRange[] }>(`/holiday/in-range`, { params }).then((response) => response.data.data)
}

/**
 * Attendance Status
 *
 */

export const fetchAttendanceStatuses = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IAttendanceStatus> }>(`/attendance-status`, { ...config, params })
    .then((response) => response.data.data)
}

export const createAttendanceStatus = (payload: Record<string, any>) => {
  return axiosBasic.post<{ data: IAttendanceStatus }>(`/attendance-status`, payload).then((response) => response.data.data)
}

export const updateAttendanceStatus = (id: string, payload: Record<string, any>) => {
  return axiosBasic.put<{ data: IAttendanceStatus }>(`/attendance-status/${id}`, payload).then((response) => response.data.data)
}

export const deleteAttendanceStatus = (id: string) => {
  return axiosBasic.delete(`/attendance-status/${id}`).then((response) => response.data.data)
}

export const fetchCalendar = (params?: { endDate?: string; startDate?: string } & IPaginationParam, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IPaginationResponse<IHoliday> }>(`/calendar`, { ...config, params }).then((response) => response.data.data)
}

/**
 * Shift Pattern
 *
 */
export const fetchShiftPatterns = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IShiftPattern> }>(`/shift-pattern`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchShiftPatternDetail = (id: string) => {
  return axiosBasic.get<{ data: IShiftPattern }>(`/shift-pattern/${id}`).then((response) => response.data.data)
}

export const createShiftPattern = (payload: Record<string, any>) => {
  return axiosBasic.post<{ data: IShiftPattern }>(`/shift-pattern`, payload).then((response) => response.data.data)
}

export const updateShiftPattern = (id: string, payload: Record<string, any>) => {
  return axiosBasic.put<{ data: IShiftPattern }>(`/shift-pattern/${id}`, payload).then((response) => response.data.data)
}

export const deleteShiftPattern = (id: string) => {
  return axiosBasic.delete(`/shift-pattern/${id}`).then((response) => response.data.data)
}

/**
 *
 * Shift Policy
 */

export const fetchShiftPolicies = (
  params?: { endDate?: string; startDate?: string; isCorrection?: boolean } & IPaginationParam,
  config?: AxiosRequestConfig,
) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IShiftPolicy> }>(`/shift-policy`, { ...config, params })
    .then((response) => response.data.data)
}

export const fetchShiftPolicy = (id: string, config?: AxiosRequestConfig) => {
  return axios.get<{ data: IShiftPolicyDetail }>(`/shift-policy/${id}`, config).then((response) => response.data.data)
}

export const createShiftPolicy = (payload: Record<string, any>) => {
  return axios.post<{ data: IShiftPolicyDetail }>(`/shift-policy`, payload).then((response) => response.data.data)
}

export const updateShiftPolicy = (id?: string, payload?: Record<string, any>) => {
  return axios.put<{ data: IShiftPolicyDetail }>(`/shift-policy/${id}`, payload).then((response) => response.data.data)
}

export const deleteShiftPolicy = (id?: string) => {
  return axios.delete(`/shift-policy/${id}`).then((response) => response.data.data)
}

/**
 * Shift Management
 *
 */
export const fetchShiftManagementIndex = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IShiftManagementIndex> }>(`/employee-shift`, { ...config, params })
    .then((response) => response.data.data)
}

export const fetchShiftManagementSwitch = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IShiftManagementSwitch> }>(`/employee-shift/switch-employee`, { ...config, params })
    .then((response) => response.data.data)
}

export const fetchShiftManagementSchedule = (params?: IPaginationShiftManagementParam, config?: AxiosRequestConfig) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IShiftManagementSchedule> }>(`/employee-shift/schedule`, { ...config, params })
    .then((response) => response.data.data)
}

export const createShiftManagement = (payload: Record<string, any>) => {
  return axios.post(`/employee-shift`, payload).then((response) => response.data.data)
}

export const adjustEmployeeShift = (payload: Record<string, any>) => {
  return axios.post(`/employee-shift/adjust`, payload).then((response) => response.data.data)
}

export const updateShiftManagement = (payload: Record<string, any>) => {
  return axios.put(`/employee-shift`, payload).then((response) => response.data.data)
}

export const deleteShiftManagement = (params: Record<string, any>) => {
  return axios.delete(`/employee-shift`, { params }).then((response) => response.data.data)
}

export const fetchOvertimes = (params?: IPaginationParam, config?: AxiosRequestConfig) => {
  return axiosBasic
    .get<{ data: IPaginationResponse<IOvertimeDataTable> }>(`/overtime-request`, { ...config, params })
    .then((response) => response.data.data)
}

export const fetchOvertime = (oid: string, params?: { data?: 'overview' | 'overtimeIn' | 'overtimeOut' }, config?: AxiosRequestConfig) => {
  return axiosBasic.get<{ data: IOvertimeDetail }>(`/overtime-request/${oid}`, { ...config, params }).then((response) => response.data.data)
}

export const createOvertime = (payload: Record<string, any>) => {
  return axiosBasic.post<{ data: IOvertimeDetail }>(`/overtime-request`, payload).then((response) => response.data.data)
}

export const updateOvertime = (id: string, payload: Record<string, any>) => {
  return axiosBasic.put<{ data: IOvertimeDetail }>(`/overtime-request/${id}`, payload).then((response) => response.data.data)
}

export const approveOvertime = (id: string, payload: Record<string, any>) => {
  return axiosBasic.patch<{ data: IOvertimeDetail }>(`/overtime-request/approval/${id}`, payload).then((response) => response.data.data)
}

export const employeeOvertime = (payload: Record<string, any>) => {
  return axiosBasic.post<{ data: IEmployeeOvertime[] }>(`/overtime-request/employees`, payload).then((response) => response.data.data)
}
